import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarPlus } from "lucide-react";
import { api } from "../../lib/api";
import {
  BOOK_KEY,
  NEXT_STEP_LABEL,
  OWNER_SOURCES,
  SOURCE_LABEL,
  browserTimeZone,
  fieldErrors,
  groupSlotsByDay,
  notConnected,
  slotProblem,
  timeLabel,
  timeZoneOptions,
  useBookStatus,
  useEventTypes,
  useOwnerSlots,
  usePersonBookings,
  useUpcomingCalls,
  whenShort,
  type BookingJson,
  type BookingSource,
} from "../../lib/bookings";
import { ADMIN_KEY } from "../../lib/admin";
import {
  Button,
  Callout,
  Card,
  CardHeader,
  CardList,
  CardRowLink,
  Checkbox,
  ErrorState,
  Field,
  Input,
  Modal,
  SelectField,
  Skeleton,
  Textarea,
  notifyError,
  toaster,
  type StatusMap,
  StatusBadge,
} from "../../components/ui";
import { ErrorSummary, type FieldIssue } from "../../components/step-editor";

/** Shared pieces for the Bookings and Scheduling pages, and the Book a call button on a person. */

export const invalidateBook = (qc: ReturnType<typeof useQueryClient>) => qc.invalidateQueries({ queryKey: ADMIN_KEY });

/** Chris's time zone (the booking app's owner setting), or the browser's until it loads. */
export function useOwnerTz(): string {
  const status = useBookStatus();
  return (status.data?.connected && status.data.settings.timezone) || browserTimeZone();
}

// --- Not connected -----------------------------------------------------------------------------

export function NotConnected({ message }: { message: string }) {
  return (
    <Card>
      <div className="p-4">
        <Callout tone="warn" title="Scheduling isn't connected">
          <p>{message}</p>
          <p className="mt-1">
            The booking app and this console share two secrets and a service binding each way. The steps are in the booking app's README, under “Connecting the Growth console”.
          </p>
        </Callout>
      </div>
    </Card>
  );
}

/** Renders `children` once the booking app answers; otherwise says why it can't be reached. */
export function BookGate({ children }: { children: ReactNode }) {
  const status = useBookStatus();
  if (status.isPending) return <Skeleton className="h-64" />;
  if (status.isError)
    return (
      <Card>
        <ErrorState title="Couldn't reach scheduling" error={status.error} />
      </Card>
    );
  if (!status.data.connected) return <NotConnected message={status.data.message} />;
  return <>{children}</>;
}

/** A query's error: "not connected" in words when that's what it is, else the usual error state. */
export function BookError({ error, title }: { error: unknown; title?: string }) {
  const nc = notConnected(error);
  if (nc) return <NotConnected message={nc.message} />;
  return <ErrorState title={title} error={error} />;
}

// --- Status --------------------------------------------------------------------------------------

export type BookingState = "upcoming" | "done" | "cancelled" | "no_show";
export const BOOKING_STATE: StatusMap<BookingState> = {
  upcoming: { label: "Upcoming", tone: "accent" },
  done: { label: "Done", tone: "neutral" },
  cancelled: { label: "Cancelled", tone: "neutral" },
  no_show: { label: "No-show", tone: "bad" },
};

export function bookingState(b: { status: string; noShow: boolean; end: string }, now = Date.now()): BookingState {
  if (b.status === "cancelled") return "cancelled";
  if (b.noShow) return "no_show";
  return Date.parse(b.end) > now ? "upcoming" : "done";
}

export const BookingStateBadge = ({ booking }: { booking: { status: string; noShow: boolean; end: string } }) => <StatusBadge map={BOOKING_STATE} status={bookingState(booking)} />;

export function SourceText({ source, createdBy, note }: { source: BookingSource; createdBy: "invitee" | "owner"; note?: string | null }) {
  return (
    <span>
      {createdBy === "invitee" ? "Booked themselves" : SOURCE_LABEL[source]}
      {note && <span className="text-ink-3"> · {note}</span>}
    </span>
  );
}

// --- Choosing a time -----------------------------------------------------------------------------

const DAY = 86_400_000;
/** The booking app answers at most 43 days at a time. */
const WINDOW_DAYS = 42;

/**
 * A day and a time from the owner's real free times for a call type, as two selects (long,
 * data-driven lists). Shown in `tz`. "Later dates" moves the window on by six weeks.
 * `bookingId` leaves that booking's own time free (moving it).
 */
export function SlotPicker({
  eventTypeId,
  tz,
  bookingId,
  value,
  onChange,
  dayId,
  timeId,
  error,
}: {
  eventTypeId: string | null;
  tz: string;
  bookingId?: string;
  value: string | null;
  onChange: (start: string | null) => void;
  dayId: string;
  timeId: string;
  error?: string;
}) {
  const [from, setFrom] = useState(() => startOfToday());
  const to = useMemo(() => new Date(Date.parse(from) + WINDOW_DAYS * DAY).toISOString(), [from]);
  const slots = useOwnerSlots(eventTypeId, from, to, bookingId);
  const days = useMemo(() => groupSlotsByDay(slots.data?.slots ?? [], tz), [slots.data, tz]);
  const [day, setDay] = useState<string | null>(null);
  const current = days.find((d) => d.date === day) ?? null;

  // A new call type or window: keep the chosen day if it's still there, else none.
  useEffect(() => {
    if (day && !days.some((d) => d.date === day)) {
      setDay(null);
      onChange(null);
    }
  }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!eventTypeId) return <p className="text-sm text-ink-3">Choose a call type to see free times.</p>;
  if (slots.isError) return <BookError error={slots.error} title="Couldn't load free times" />;
  const later = (
    <Button size="sm" variant="ghost" onClick={() => setFrom(to)}>
      Later dates
    </Button>
  );
  const earlier = from !== startOfToday() && (
    <Button size="sm" variant="ghost" onClick={() => setFrom(new Date(Math.max(Date.parse(startOfToday()), Date.parse(from) - WINDOW_DAYS * DAY)).toISOString())}>
      Earlier dates
    </Button>
  );
  return (
    <div className="space-y-3">
      <div className="grid gap-3 @xl:grid-cols-2">
        <Field label="Day" id={dayId} error={error && !day ? error : undefined}>
          <SelectField
            className="w-full"
            placeholder={slots.isPending ? "Loading free days…" : days.length ? "Choose a day" : "No free days"}
            items={days.map((d) => ({ value: d.date, label: `${d.label} (${d.slots.length} free)` }))}
            value={day ?? undefined}
            onChange={(v) => {
              setDay(v);
              onChange(null);
            }}
          />
        </Field>
        <Field label="Time" id={timeId} error={error && day ? error : undefined} hint={`Your time (${tz.replace(/_/g, " ")})`}>
          <SelectField
            className="w-full"
            placeholder={current ? "Choose a time" : "Choose a day first"}
            items={(current?.slots ?? []).map((s) => ({ value: s.start, label: `${timeLabel(s.start, tz)} to ${timeLabel(s.end, tz)}` }))}
            value={value ?? undefined}
            onChange={(v) => onChange(v)}
          />
        </Field>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-ink-3">
        <span role="status">
          {slots.isPending ? "Loading free times…" : `${days.length ? `${days.length} day${days.length === 1 ? "" : "s"} with free times` : "No free times"} between ${dayLabelShort(from, tz)} and ${dayLabelShort(to, tz)}.`}
        </span>
        {earlier}
        {later}
      </div>
    </div>
  );
}

function startOfToday(): string {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  return d.toISOString();
}
const dayLabelShort = (iso: string, tz: string) => new Intl.DateTimeFormat("en-GB", { timeZone: tz, day: "numeric", month: "short" }).format(new Date(iso));

// --- Book someone in -----------------------------------------------------------------------------

export interface BookPrefill {
  name?: string | null;
  email?: string | null;
}

/**
 * "Book someone in": after a cold call, a cold email or a meetup. Free times come from the booking
 * app (your hours, the calendar and other bookings; no notice period). The booking goes through
 * the same path as the booking page, so nobody else can take the time at the same moment.
 */
export function BookSomeoneIn({ open, onOpenChange, prefill, onBooked }: { open: boolean; onOpenChange: (o: boolean) => void; prefill?: BookPrefill; onBooked?: (b: BookingJson) => void }) {
  const qc = useQueryClient();
  const ownerTz = useOwnerTz();
  const types = useEventTypes(open);
  const active = (types.data?.eventTypes ?? []).filter((t) => t.active);
  const blank = () => ({ eventTypeId: "", start: null as string | null, name: prefill?.name ?? "", email: prefill?.email ?? "", timezone: ownerTz, source: "" as string, sourceNote: "", notify: true });
  const [form, setForm] = useState(blank);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [issues, setIssues] = useState<FieldIssue[]>([]);
  const [saving, setSaving] = useState(false);
  const key = useRef(crypto.randomUUID());
  const summary = useRef<HTMLDivElement>(null);
  const set = <K extends keyof ReturnType<typeof blank>>(k: K, v: ReturnType<typeof blank>[K]) => setForm((f) => ({ ...f, [k]: v }));

  // Each time it opens: a fresh form (with the person's details when there are some) and a fresh key.
  useEffect(() => {
    if (!open) return;
    setForm(blank());
    setErrors({});
    setIssues([]);
    key.current = crypto.randomUUID();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  // One active call type: choose it.
  useEffect(() => {
    if (open && !form.eventTypeId && active.length === 1) set("eventTypeId", active[0]!.id);
  }, [open, active.length]); // eslint-disable-line react-hooks/exhaustive-deps
  // The form appears once the call types load, after the dialog has placed focus: move it to the first field.
  const ready = open && !!types.data;
  useEffect(() => {
    if (ready) requestAnimationFrame(() => document.getElementById(active.length === 1 ? ids.name : ids.eventTypeId)?.focus());
  }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // A missing time jumps to the day: the time list fills once a day is chosen.
  const ids = { eventTypeId: "bsi-type", start: "bsi-day", day: "bsi-day", name: "bsi-name", email: "bsi-email", timezone: "bsi-tz", source: "bsi-source", sourceNote: "bsi-note" };
  const LABEL: Record<string, string> = { eventTypeId: "Call type", start: "Time", name: "Name", email: "Email", timezone: "Their time zone", source: "Where you met them", sourceNote: "Note" };

  const show = (found: Record<string, string>) => {
    setErrors(found);
    const list = Object.entries(found).map(([k, message]) => ({ id: ids[k as keyof typeof ids] ?? ids.name, label: LABEL[k] ?? k, message }));
    setIssues(list);
    requestAnimationFrame(() => summary.current?.focus());
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    const found: Record<string, string> = {};
    if (!form.eventTypeId) found.eventTypeId = "Choose a call type";
    if (form.eventTypeId && !form.start) found.start = "Choose a day and a time";
    if (!form.name.trim()) found.name = "Enter their name";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) found.email = "Enter an email address like name@example.com";
    if (!form.source) found.source = "Choose where you met them";
    if (Object.keys(found).length) return show(found);
    setSaving(true);
    try {
      const { booking } = await api<{ booking: BookingJson }>("/admin/book/bookings", {
        method: "POST",
        json: { eventTypeId: form.eventTypeId, start: form.start, timezone: form.timezone, name: form.name.trim(), email: form.email.trim(), source: form.source, sourceNote: form.sourceNote.trim() || null, notify: form.notify, idempotencyKey: key.current },
      });
      toaster.success({
        title: `Booked: ${booking.name ?? booking.email}`,
        description: `${whenShort(booking.start, booking.end, ownerTz)}${form.notify ? ". They've been sent the invite." : ". No email sent."}`,
      });
      await invalidateBook(qc);
      onOpenChange(false);
      onBooked?.(booking);
    } catch (err) {
      const fields = fieldErrors(err);
      const slot = slotProblem(err);
      if (Object.keys(fields).length) show(fields);
      else if (slot) {
        await qc.invalidateQueries({ queryKey: [...BOOK_KEY, "slots"] });
        set("start", null);
        show({ start: slot });
      } else notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Book someone in" description="After a call, an email or a meetup. Times are your free times, and you can book inside your usual notice period." wide>
      {types.isError ? (
        <BookError error={types.error} title="Couldn't load call types" />
      ) : types.isPending ? (
        <Skeleton className="h-64" />
      ) : !active.length ? (
        <Callout tone="info">There are no active call types. Add one in Scheduling first.</Callout>
      ) : (
        <form onSubmit={submit} noValidate className="@container space-y-4">
          {!!issues.length && <ErrorSummary ref={summary} issues={issues} title={issues.length === 1 ? "There is 1 problem" : `There are ${issues.length} problems`} className="" />}
          <Field label="Call type" id={ids.eventTypeId} error={errors.eventTypeId} required>
            <SelectField
              className="w-full"
              placeholder="Choose a call type"
              items={active.map((t) => ({ value: t.id, label: `${t.title} (${t.durationMin} min)` }))}
              value={form.eventTypeId || undefined}
              onChange={(v) => setForm((f) => ({ ...f, eventTypeId: v, start: null }))}
            />
          </Field>
          <SlotPicker eventTypeId={form.eventTypeId || null} tz={ownerTz} value={form.start} onChange={(s) => set("start", s)} dayId={ids.day} timeId="bsi-time" error={errors.start} />
          <div className="grid gap-3 @xl:grid-cols-2">
            <Field label="Name" id={ids.name} error={errors.name} required>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} autoComplete="off" />
            </Field>
            <Field label="Email" id={ids.email} error={errors.email} required>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="off" placeholder="ada@quillmere.example" />
            </Field>
            <Field label="Their time zone" id={ids.timezone} hint="Their emails show times in this zone." error={errors.timezone}>
              <SelectField className="w-full" items={timeZoneOptions(form.timezone)} value={form.timezone} onChange={(v) => set("timezone", v)} />
            </Field>
            <Field label="Where you met them" id={ids.source} error={errors.source} required>
              <SelectField className="w-full" placeholder="Choose one" items={OWNER_SOURCES.map((s) => ({ value: s, label: SOURCE_LABEL[s] }))} value={form.source || undefined} onChange={(v) => set("source", v)} />
            </Field>
          </div>
          <Field label="Note (optional)" id={ids.sourceNote} hint="Which meetup, who referred them. Shown on their timeline in People." error={errors.sourceNote}>
            <Input value={form.sourceNote} onChange={(e) => set("sourceNote", e.target.value)} maxLength={500} placeholder="Brighton accessibility meetup" />
          </Field>
          <Checkbox checked={form.notify} onCheckedChange={(c) => set("notify", c)} hint="The confirmation with a calendar file and the Meet link, and a reminder the day before.">
            Email them the invite
          </Checkbox>
          <div className="flex justify-end gap-2">
            <Button type="button" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" pending={saving} pendingLabel="Booking…">
              Book call
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

// --- Move and cancel -----------------------------------------------------------------------------

export function MoveBooking({ booking, open, onOpenChange }: { booking: BookingJson; open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const tz = useOwnerTz();
  const [start, setStart] = useState<string | null>(null);
  const [notify, setNotify] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setStart(null);
      setNotify(!booking.detailsDeleted);
      setError(undefined);
    }
  }, [open, booking.detailsDeleted]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    if (!start) {
      setError("Choose a day and a time");
      requestAnimationFrame(() => document.getElementById("move-day")?.focus());
      return;
    }
    setSaving(true);
    try {
      const { booking: b } = await api<{ booking: BookingJson }>(`/admin/book/bookings/${booking.id}/reschedule`, { method: "POST", json: { start, notify } });
      toaster.success({ title: "Call moved", description: `Now ${whenShort(b.start, b.end, tz)}.${notify ? " They've been emailed." : ""}` });
      await invalidateBook(qc);
      onOpenChange(false);
    } catch (err) {
      const slot = slotProblem(err);
      if (slot) {
        await qc.invalidateQueries({ queryKey: [...BOOK_KEY, "slots"] });
        setStart(null);
        setError(slot);
        requestAnimationFrame(() => document.getElementById("move-time")?.focus());
      } else notifyError(err);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Move this call" description={`Now ${whenShort(booking.start, booking.end, tz)}. Choose a new time from your free times.`} wide>
      <form onSubmit={submit} noValidate className="@container space-y-4">
        <SlotPicker eventTypeId={booking.eventType.id} bookingId={booking.id} tz={tz} value={start} onChange={(s) => { setStart(s); setError(undefined); }} dayId="move-day" timeId="move-time" error={error} />
        <Checkbox checked={notify} onCheckedChange={setNotify} disabled={booking.detailsDeleted} hint="A “Your call has moved” email with the new calendar file.">
          Email them about the change
        </Checkbox>
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={() => onOpenChange(false)}>
            Keep the time
          </Button>
          <Button type="submit" variant="primary" pending={saving} pendingLabel="Moving…">
            Move call
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function CancelBooking({ booking, open, onOpenChange }: { booking: BookingJson; open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setReason("");
      setNotify(!booking.detailsDeleted);
    }
  }, [open, booking.detailsDeleted]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await api(`/admin/book/bookings/${booking.id}/cancel`, { method: "POST", json: { reason: reason.trim() || null, notify } });
      toaster.success({ title: "Call cancelled", description: notify ? "They've been emailed, with a link to book another time." : "No email sent." });
      await invalidateBook(qc);
      onOpenChange(false);
    } catch (err) {
      const slot = slotProblem(err);
      if (slot) toaster.error({ title: "Couldn't cancel", description: slot });
      else notifyError(err);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal open={open} onOpenChange={onOpenChange} title={`Cancel the call with ${booking.name ?? "this person"}?`} description="The time is freed and the calendar event is removed.">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Reason (optional)" hint="Kept with the booking and on their timeline. Not sent to them.">
          <Textarea data-autofocus value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={1000} />
        </Field>
        <Checkbox checked={notify} onCheckedChange={setNotify} disabled={booking.detailsDeleted} hint="Says you had to cancel, with a link to book another time.">
          Email them that it's cancelled
        </Checkbox>
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={() => onOpenChange(false)}>
            Keep call
          </Button>
          <Button type="submit" variant="danger" pending={saving} pendingLabel="Cancelling…">
            Cancel call
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Marks a started call as a no-show, or clears it, at once (reversible, so no confirmation). */
export function useNoShow(booking: BookingJson) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    const next = !booking.noShow;
    try {
      await api(`/admin/book/bookings/${booking.id}/no-show`, { method: "POST", json: { noShow: next } });
      toaster.success({ title: next ? "Marked as a no-show" : "No-show cleared", description: next ? "A follow-up task is on their timeline in People." : undefined });
      await invalidateBook(qc);
    } catch (err) {
      notifyError(err);
    } finally {
      setBusy(false);
    }
  };
  return { busy, toggle };
}

// --- On a person, and on the overview (from the copy the booking events keep) --------------------

/** A person's calls, newest first, with what they answered in the survey after each, and "Book a call" with their name and email filled in. */
export function PersonBookings({ contactId, onBook }: { contactId: string; onBook: () => void }) {
  const q = usePersonBookings(contactId);
  const tz = useOwnerTz();
  return (
    <Card>
      <CardHeader
        title="Bookings"
        actions={
          <Button size="sm" onClick={onBook}>
            <CalendarPlus size={13} aria-hidden /> Book a call
          </Button>
        }
      />
      {q.isError ? (
        <ErrorState title="Couldn't load their bookings" error={q.error} />
      ) : (
        <CardList loading={q.isPending} empty="No calls booked.">
          {q.data?.bookings.map((b) => (
            <CardRowLink key={b.id} to={`/admin/bookings/${b.id}`} className="flex-wrap">
              <span className="min-w-0">
                <span className="block text-sm text-ink">{b.eventType.title}</span>
                <span className="block text-xs text-ink-3">{whenShort(b.start, b.end, tz)}</span>
                {b.survey && (
                  <span className="block text-xs text-ink-2">
                    Survey: rated it {b.survey.usefulness} of 5{b.survey.nextStep ? `, ${NEXT_STEP_LABEL[b.survey.nextStep].toLowerCase()}` : ""}
                  </span>
                )}
              </span>
              <BookingStateBadge booking={b} />
            </CardRowLink>
          ))}
        </CardList>
      )}
    </Card>
  );
}

/** The next few calls, for the overview. */
export function UpcomingCalls() {
  const q = useUpcomingCalls();
  const tz = useOwnerTz();
  return (
    <Card>
      <CardHeader title="Upcoming calls" subtitle="Your next calls, in your time" actions={<Link to="/admin/bookings" className="text-xs text-accent-text hover:underline">All bookings</Link>} />
      {q.isError ? (
        <ErrorState title="Couldn't load upcoming calls" error={q.error} />
      ) : (
        <CardList loading={q.isPending} empty="No calls booked.">
          {q.data?.bookings.map((b) => (
            <CardRowLink key={b.id} to={`/admin/bookings/${b.id}`} className="flex-wrap">
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-ink">{b.name ?? b.email ?? "Details deleted"}</span>
                <span className="block text-xs text-ink-3">{b.eventType.title}</span>
              </span>
              <span className="text-xs text-ink-2 tabular">{whenShort(b.start, b.end, tz)}</span>
            </CardRowLink>
          ))}
        </CardList>
      )}
    </Card>
  );
}
